// FILE PATH: eRFQFrontend/src/component/Logistic/ViewLogisticQuotes.jsx

import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import ErrorModal from "../../utils/ErrorModal";
import SuccessModal from "../../utils/SuccessModal";
import { handleExportPDF } from "../../utils/exportToPDF";
import ViewLogisticQuotesTable from "./ViewLogisticQuotesTable";
import LogisticSummaryTable from "./LogisticSummaryTable";
import ViewLogisticQuotesTableExport from "./ViewLogisticQuotesTableExport";
import { useDownloadExcel } from "react-export-table-to-excel";

const parseArrayField = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const withUnit = (value, unit) => {
  if (value === null || value === undefined || value === "") return "";
  return `${value} ${unit}`;
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const ViewLogisticQuotes = () => {
  const outletContext = useOutletContext();
  const storedUser =
    outletContext?.storedUser ?? JSON.parse(localStorage.getItem("user"));

  const navigate = useNavigate();
  const { logisticDraftId } = useParams();
  const awardRef = useRef();
  const tableRef = useRef(null);

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [actionType, setActionType] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [logisticDraft, setLogisticDraft] = useState(null);
  const [comments, setComments] = useState([]);
  const [reason, setReason] = useState("");

  // eAuction / Revision / Approval UI states (same structure as eRFQ flow)
  const [applyValue, setApplyValue] = useState(false);
  const [openAuction, setOpenAuction] = useState(false);
  const [openRebidModal, setOpenRebidModal] = useState(false);
  const [rebidData, setRebidData] = useState({
    rebidStartDate: "",
    rebidEndDate: "",
    rebidComment: "",
  });
  const [errors, setErrors] = useState({});

  const comparisonQuotes = useMemo(() => {
    const vendorGroups = new Map();

    (quotes || []).forEach((quote) => {
      const vendorKey =
        quote.vendorEmail ||
        quote.vendorFirmName ||
        quote.vendorName ||
        quote.logisticQuoteId;
      if (!vendorGroups.has(vendorKey)) {
        vendorGroups.set(vendorKey, []);
      }
      vendorGroups.get(vendorKey).push(quote);
    });

    return Array.from(vendorGroups.values()).flatMap((vendorQuotes) => {
      const sortedQuotes = vendorQuotes
        .slice()
        .sort(
          (a, b) =>
            new Date(a.createdDate || 0).getTime() -
            new Date(b.createdDate || 0).getTime(),
        );

      const vendorName =
        sortedQuotes[sortedQuotes.length - 1]?.vendorFirmName ||
        sortedQuotes[sortedQuotes.length - 1]?.vendorName ||
        "Vendor";

      if (sortedQuotes.length === 1) {
        return [
          {
            ...sortedQuotes[0],
            comparisonLabel: vendorName,
            isCurrentVersion: true,
          },
        ];
      }

      const previousQuote = sortedQuotes[0];
      const currentQuote = sortedQuotes[sortedQuotes.length - 1];

      return [
        {
          ...previousQuote,
          comparisonLabel: `${vendorName} (Initial Quote)`,
          isCurrentVersion: false,
        },
        {
          ...currentQuote,
          comparisonLabel: `${vendorName} (Final Quote)`,
          isCurrentVersion: true,
        },
      ];
    });
  }, [quotes]);

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: "Quotes Comparison",
    sheet: "Quotes Comparison",
  });

  const getQuotes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/logistic/${logisticDraftId}/quotes`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setQuotes(res.data || []);
    } catch (error) {
      console.error(error);
      setErrorMsg(error?.response?.data?.Exception || "Failed to load quotes");
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };

  const getLogisticDraft = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/logistic-draft/get-by-id?logisticDraftId=${logisticDraftId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setLogisticDraft(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getComments = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/comment/logistic?logisticDraftId=${logisticDraftId}&username=${storedUser.username}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setComments(res.data || []);
    } catch (error) {
      setComments([]);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  useEffect(() => {
    getQuotes();
    getLogisticDraft();
    getComments();
  }, [logisticDraftId]);

  const submitQuoteAction = async ({ quote, type, comment = "" }) => {
    if (type !== "SUBMITTED" && !quote) return;
    if (type !== "AWARDED" && !comment.trim()) {
      setErrors((prev) => ({ ...prev, reason: "Reason is required" }));
      return;
    }

    setBtnLoading(true);
    try {
      if (type === "SUBMITTED") {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/logistic/quote/query-to-buyer`,
          {
            logisticDraftId,
            username: storedUser.username,
            reason: comment.trim(),
          },
          {
            headers: { Authorization: `Bearer ${storedUser.accessToken}` },
          },
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/logistic/quote/status`,
          {
            quoteId: quote.logisticQuoteId,
            status: type,
            username: storedUser.username,
            reason: comment.trim(),
          },
          {
            headers: { Authorization: `Bearer ${storedUser.accessToken}` },
          },
        );
      }
      setOpen(false);

      const vendorName = quote?.vendorFirmName || quote?.vendorName;
      if (type === "SUBMITTED") {
        setSuccessMsg("Quotes sent back to buyer for revision.");
      } else if (type === "DISQUALIFIED") {
        setSuccessMsg(`Quote from ${vendorName} has been rejected.`);
      } else if (type === "AWARDED") {
        setSuccessMsg(`Quote from ${vendorName} has been awarded.`);
      } else {
        setSuccessMsg(`Quote from ${vendorName} has been recommended.`);
      }

      setOpenSuccess(true);
      await getQuotes();
      await getLogisticDraft();
      await getComments();
    } catch (error) {
      console.error(error);
      setErrorMsg(error?.response?.data?.Exception || "Action failed");
      setOpenError(true);
    } finally {
      setBtnLoading(false);
      setSelectedQuote(null);
      setReason("");
    }
  };

  const handleAction = (quote, type) => {
    setSelectedQuote(quote);
    setActionType(type);
    setReason("");
    setErrors((prev) => ({ ...prev, reason: "" }));
    setOpen(true);
  };

  const confirmAction = async () => {
    await submitQuoteAction({
      quote: selectedQuote,
      type: actionType,
      comment: reason,
    });
  };

  const handleRebidChange = (e) => {
    setRebidData({ ...rebidData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleCloseBids = async () => {
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/logistic/close-bid`,
        null,
        {
          params: {
            logisticDraftId,
            username: storedUser.username,
          },
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );
      setSuccessMsg("Bids closed successfully");
      setOpenSuccess(true);
      await getLogisticDraft();
    } catch (error) {
      console.error(error);
      setErrorMsg(error?.response?.data?.Exception || "Failed to close bids");
      setOpenError(true);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleRebidSubmit = async () => {
    if (!rebidData.rebidStartDate || !rebidData.rebidEndDate) {
      setErrors({
        rebidStartDate: !rebidData.rebidStartDate
          ? "Start date is required"
          : "",
        rebidEndDate: !rebidData.rebidEndDate ? "End date is required" : "",
      });
      return;
    }

    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/logistic/rebid`,
        {
          logisticDraftId,
          username: storedUser.username,
          rebidStartDate: rebidData.rebidStartDate,
          rebidEndDate: rebidData.rebidEndDate,
          rebidComment: rebidData.rebidComment,
        },
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );
      setOpenRebidModal(false);
      setRebidData({ rebidStartDate: "", rebidEndDate: "", rebidComment: "" });
      setSuccessMsg("Rebid initiated successfully");
      setOpenSuccess(true);
      await getQuotes();
      await getLogisticDraft();
    } catch (error) {
      console.error(error);
      setErrorMsg(
        error?.response?.data?.Exception || "Failed to initiate rebid",
      );
      setOpenError(true);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleSendEauction = async () => {
    if (!rebidData.rebidEndDate) {
      setErrors({ ...errors, rebidEndDate: "End date is required" });
      return;
    }

    try {
      setBtnLoading(true);
      const normalizedEndDate =
        rebidData.rebidEndDate && rebidData.rebidEndDate.length === 16
          ? `${rebidData.rebidEndDate}:00`
          : rebidData.rebidEndDate;
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/logistic/eauction`,
        null,
        {
          params: {
            logisticDraftId,
            endDate: normalizedEndDate,
          },
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );
      setOpenAuction(false);
      setSuccessMsg("E-Auction request submitted successfully");
      setOpenSuccess(true);
      await getLogisticDraft();
    } catch (error) {
      console.error(error);
      setErrorMsg(
        error?.response?.data?.Exception || "E-Auction request failed",
      );
      setOpenError(true);
    } finally {
      setBtnLoading(false);
    }
  };

  const validateForm = (formType = "") => {
    const newErrors = {};
    const data = rebidData || {};
    const now = new Date();

    if (formType === "rebidForm") {
      if (!data.rebidStartDate)
        newErrors.rebidStartDate = "Start Date is required";
      if (!data.rebidEndDate) newErrors.rebidEndDate = "End Date is required";
      if (data.rebidStartDate && new Date(data.rebidStartDate) < now) {
        newErrors.rebidStartDate = "Start date must be in the future";
      }
      if (
        data.rebidEndDate &&
        new Date(data.rebidEndDate) <= new Date(data.rebidStartDate || now)
      ) {
        newErrors.rebidEndDate = "End date must be after start date";
      }
    }
    return newErrors;
  };

  const handleSendEauctionWithValidation = async () => {
    const formErrors = validateForm("auctionForm");
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    await handleSendEauction();
  };

  const anyAwarded = quotes.some((q) => q.quoteStatus === "AWARDED");
  const draftStatus = (logisticDraft?.status || "").toUpperCase();
  const now = new Date();
  const hasSubmittedQuote = quotes.some((q) =>
    ["SUBMITTED", "BID_SUBMITTED", "QUOTE_SUBMITTED", "REBID"].includes(
      (q.quoteStatus || "").toUpperCase(),
    ),
  );
  const isRebidOrEauctionFlow = ["REBIDDING", "EAUCTIONED"].includes(
    draftStatus,
  );
  const isPostCloseBidStage = draftStatus === "BIDS_CLOSED";
  const canShowCloseBids =
    storedUser.userRole[0] === "BUYER" &&
    (now <= new Date(logisticDraft?.submissionDate) ||
      !logisticDraft?.rebidEndDate ||
      now <= new Date(logisticDraft?.rebidEndDate)) &&
    draftStatus !== "BIDS_CLOSED" &&
    draftStatus !== "AWARDED";
  const canShowPostBidActions =
    draftStatus === "BIDS_CLOSED" &&
    draftStatus !== "AWARDED" &&
    storedUser.userRole[0] === "BUYER";
  const userRole = storedUser?.userRole?.[0] || "";
  const hasApproved1 = quotes.some(
    (q) => (q.quoteStatus || "").toUpperCase() === "APPROVED_1",
  );
  const hasApproved2 = quotes.some(
    (q) => (q.quoteStatus || "").toUpperCase() === "APPROVED_2",
  );
  const hasApproved = quotes.some(
    (q) => (q.quoteStatus || "").toUpperCase() === "APPROVED",
  );
  const hasAnyReviewStageQuote = hasApproved1 || hasApproved2 || hasApproved;
  const canShowQueryToBuyer =
    isPostCloseBidStage &&
    draftStatus !== "AWARDED" &&
    userRole !== "BUYER" &&
    hasAnyReviewStageQuote;
  const showQuoteActions =
    isPostCloseBidStage && !isRebidOrEauctionFlow && !anyAwarded;
  const totalVendorsInvited = logisticDraft?.totalVendors;
  const participatedCount = new Set(
    quotes.map(
      (q) =>
        q.vendorEmail || q.vendorFirmName || q.vendorName || q.logisticQuoteId,
    ),
  ).size;
  const currencyArray = parseArrayField(logisticDraft?.currencies);

  return (
    <div style={{ position: "relative" }}>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF... This may take a while.{" "}
          <CircularProgress color="#fff" />
        </div>
      )}
      {exportingExcel && (
        <div className="uploading-div">
          Exporting Excel... This may take a while.{" "}
          <CircularProgress color="#fff" />
        </div>
      )}
      {btnLoading && <div className="loading-div"></div>}

      <div style={{ display: "flex", justifyContent: "end", gap: 10 }}>
        <Button
          className="btn-contained"
          sx={{ width: "auto" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(awardRef, "Quotes_Comparison");
            } catch (error) {
              console.log(error);
            } finally {
              setExporting(false);
            }
          }}
          disabled={!quotes.length}
        >
          Export Comparatives PDF
        </Button>

        <Button
          className="btn-contained"
          sx={{ width: "auto" }}
          onClick={() => {
            try {
              setExportingExcel(true);
              onDownload();
            } catch (error) {
              console.log(error);
            } finally {
              setExportingExcel(false);
            }
          }}
        >
          Export Comparatives Excel
        </Button>
      </div>

      <Typography variant="h6">Quotes Received</Typography>

      {currencyArray.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Currency Exchange Rates
          </Typography>
          <Box className="dashboard-container" px={10}>
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px" }}>Currency</th>
                  <th>Exchange Rate</th>
                </tr>
              </thead>
              <tbody>
                {currencyArray.map((currency, index) => (
                  <tr key={index} style={{ textAlign: "center" }}>
                    <td style={{ padding: "20px" }}>{currency.name || "-"}</td>
                    <td>{currency.rate ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      )}

      {logisticDraft && (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Common Logistics Details
          </Typography>
          <Box className="dashboard-container" px={10}>
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px", width: "40%" }}>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Mode of Consignment", logisticDraft.modeOfConsignment],
                  ["Invoice No", logisticDraft.invoiceNo],
                  ["Origin", logisticDraft.origin],
                  ["Destination", logisticDraft.destination],
                  ["Consignee", logisticDraft.consignee],
                  ["Transit Time", withUnit(logisticDraft.transitTime, "Days")],
                  [
                    "Gross Weight (kg)",
                    withUnit(logisticDraft.grossWeight, "Kg"),
                  ],
                  ["Length", withUnit(logisticDraft.length, "inches")],
                  ["Breadth", withUnit(logisticDraft.breadth, "inches")],
                  ["Height", withUnit(logisticDraft.height, "inches")],
                  ["Volume (CBM)", withUnit(logisticDraft.volumeCBM, "CBM")],
                  ["No. of Pallets", logisticDraft.noOfPallets],
                  ["Address", logisticDraft.address],
                ].map(([label, value]) => (
                  <tr key={label} style={{ textAlign: "center" }}>
                    <td
                      style={{
                        padding: "20px",
                        fontWeight: "bold",
                        textAlign: "left",
                      }}
                    >
                      {label}
                    </td>
                    <td>{value ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      )}

      <Box className="dashboard-container" py={10} px={2}>
        {comments &&
          comments.map((c) => (
            <Box
              key={c.id}
              className="dashboard-container"
              style={{
                background: "#F6F6F6",
                borderRadius: "0",
                padding: "10px 20px",
                marginBottom: "12px",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                Comment from: {c.orgUser?.name} ({formatDateTime(c.createdDate)}
                )
              </Typography>
              <Typography pt={0}>{c.comment}</Typography>
            </Box>
          ))}

        {quotes.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              marginBottom: "30px",
              fontWeight: "bold",
            }}
          >
            Vendors Participated:{" "}
            {`${participatedCount}/${totalVendorsInvited}`}
          </div>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            Loading...
          </Box>
        ) : quotes.length === 0 ? (
          <Typography variant="h6" textAlign="center" mt={5}>
            No vendor quotes for the RFQ
          </Typography>
        ) : (
          <>
            <div
              ref={awardRef}
              style={{
                position: "absolute",
                top: "-9999px",
                left: "-9999px",
                height: "auto",
                width: "auto",
                paddingInline: "5px",
              }}
            >
              {currencyArray.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 3 }}>
                    Currency Exchange Rates
                  </Typography>
                  <Box className="dashboard-container" px={10}>
                    <table
                      border={1}
                      style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        marginTop: "10px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ padding: "20px" }}>Currency</th>
                          <th>Exchange Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currencyArray.map((currency, index) => (
                          <tr key={index} style={{ textAlign: "center" }}>
                            <td style={{ padding: "20px" }}>
                              {currency.name || "-"}
                            </td>
                            <td>{currency.rate ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}
              {logisticDraft && (
                <>
                  <Typography variant="h6" sx={{ mt: 3 }}>
                    Common Logistics Details
                  </Typography>
                  <Box className="dashboard-container" px={10}>
                    <table
                      border={1}
                      style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        marginTop: "10px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ padding: "20px", width: "40%" }}>
                            Field
                          </th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          [
                            "Mode of Consignment",
                            logisticDraft.modeOfConsignment,
                          ],
                          ["Invoice No", logisticDraft.invoiceNo],
                          ["Origin", logisticDraft.origin],
                          ["Destination", logisticDraft.destination],
                          ["Consignee", logisticDraft.consignee],
                          [
                            "Transit Time",
                            withUnit(logisticDraft.transitTime, "Days"),
                          ],
                          [
                            "Gross Weight (kg)",
                            withUnit(logisticDraft.grossWeight, "Kg"),
                          ],
                          ["Length", withUnit(logisticDraft.length, "inches")],
                          [
                            "Breadth",
                            withUnit(logisticDraft.breadth, "inches"),
                          ],
                          ["Height", withUnit(logisticDraft.height, "inches")],
                          [
                            "Volume (CBM)",
                            withUnit(logisticDraft.volumeCBM, "CBM"),
                          ],
                          ["No. of Pallets", logisticDraft.noOfPallets],
                          ["Address", logisticDraft.address],
                        ].map(([label, value]) => (
                          <tr key={label} style={{ textAlign: "center" }}>
                            <td
                              style={{
                                padding: "20px",
                                fontWeight: "bold",
                                textAlign: "left",
                              }}
                            >
                              {label}
                            </td>
                            <td>{value ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}
              <ViewLogisticQuotesTableExport
                quotes={comparisonQuotes}
                logisticDraft={logisticDraft}
                currencyArray={currencyArray}
              />
            </div>

            <div style={{ display: "none" }}>
              <div ref={tableRef}>
                {currencyArray.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3 }}>
                      Currency Exchange Rates
                    </Typography>
                    <Box className="dashboard-container" px={10}>
                      <table
                        border={1}
                        style={{
                          borderCollapse: "collapse",
                          width: "100%",
                          marginTop: "10px",
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={{ padding: "20px" }}>Currency</th>
                            <th>Exchange Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currencyArray.map((currency, index) => (
                            <tr key={index} style={{ textAlign: "center" }}>
                              <td style={{ padding: "20px" }}>
                                {currency.name || "-"}
                              </td>
                              <td>{currency.rate ?? "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </>
                )}
                {logisticDraft && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3 }}>
                      Common Logistics Details
                    </Typography>
                    <Box className="dashboard-container" px={10}>
                      <table
                        border={1}
                        style={{
                          borderCollapse: "collapse",
                          width: "100%",
                          marginTop: "10px",
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={{ padding: "20px", width: "40%" }}>
                              Field
                            </th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            [
                              "Mode of Consignment",
                              logisticDraft.modeOfConsignment,
                            ],
                            ["Invoice No", logisticDraft.invoiceNo],
                            ["Origin", logisticDraft.origin],
                            ["Destination", logisticDraft.destination],
                            ["Consignee", logisticDraft.consignee],
                            [
                              "Transit Time",
                              withUnit(logisticDraft.transitTime, "Days"),
                            ],
                            [
                              "Gross Weight (kg)",
                              withUnit(logisticDraft.grossWeight, "Kg"),
                            ],
                            [
                              "Length",
                              withUnit(logisticDraft.length, "inches"),
                            ],
                            [
                              "Breadth",
                              withUnit(logisticDraft.breadth, "inches"),
                            ],
                            [
                              "Height",
                              withUnit(logisticDraft.height, "inches"),
                            ],
                            [
                              "Volume (CBM)",
                              withUnit(logisticDraft.volumeCBM, "CBM"),
                            ],
                            ["No. of Pallets", logisticDraft.noOfPallets],
                            ["Address", logisticDraft.address],
                          ].map(([label, value]) => (
                            <tr key={label} style={{ textAlign: "center" }}>
                              <td
                                style={{
                                  padding: "20px",
                                  fontWeight: "bold",
                                  textAlign: "left",
                                }}
                              >
                                {label}
                              </td>
                              <td>{value ?? "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </>
                )}
                <ViewLogisticQuotesTableExport
                  quotes={comparisonQuotes}
                  logisticDraft={logisticDraft}
                  currencyArray={currencyArray}
                />
              </div>
            </div>

            <LogisticSummaryTable
              quotes={comparisonQuotes}
              logisticDraft={logisticDraft}
            />
            <ViewLogisticQuotesTable
              quotes={comparisonQuotes}
              storedUser={storedUser}
              anyAwarded={anyAwarded}
              showActions={showQuoteActions}
              onAction={handleAction}
              logisticApprovers={logisticDraft?.logisticApprovers}
              logisticDraft={logisticDraft}
            />
          </>
        )}

        {quotes.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              marginTop: "30px",
              fontWeight: "bold",
            }}
          >
            Vendors Participated:{" "}
            {`${participatedCount}/${totalVendorsInvited}`}
          </div>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Box sx={{ display: "flex", gap: 5 }}>
          <Button
            className="btn-outlined"
            onClick={() => navigate("/logistic-dashboard")}
          >
            Back
          </Button>
          {canShowCloseBids && (
            <Button
              className="btn-contained"
              onClick={handleCloseBids}
              disabled={btnLoading || participatedCount !== totalVendorsInvited}
            >
              Close Bids
            </Button>
          )}
          {canShowPostBidActions && (
            <>
              <Button
                className="btn-outlined"
                onClick={() => setOpenRebidModal(true)}
                disabled={btnLoading || !hasSubmittedQuote}
              >
                Request Revised RFQ
              </Button>
              <Button
                className="btn-outlined"
                onClick={() => setOpenAuction(true)}
                disabled={btnLoading || !hasSubmittedQuote}
              >
                E-Auction
              </Button>
            </>
          )}
          {canShowQueryToBuyer && (
            <Button
              className="btn-outlined"
              onClick={() => {
                setSelectedQuote(null);
                setActionType("SUBMITTED");
                setReason("");
                setErrors((prev) => ({ ...prev, reason: "" }));
                setOpen(true);
              }}
              disabled={btnLoading}
            >
              Query to Buyer
            </Button>
          )}
        </Box>
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" textAlign="start">
            Enter your comments for{" "}
            {actionType === "SUBMITTED"
              ? "Reverting the Quotes"
              : actionType === "DISQUALIFIED"
                ? `Reject quote from ${selectedQuote?.vendorFirmName || selectedQuote?.vendorName}?`
                : actionType === "AWARDED"
                  ? `Award quote from ${selectedQuote?.vendorFirmName || selectedQuote?.vendorName}?`
                  : `Recommend quote from ${selectedQuote?.vendorFirmName || selectedQuote?.vendorName}?`}
          </Typography>
          <textarea
            className={`${errors.reason && "input-error"}`}
            name="reason"
            id="reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setErrors((prev) => ({ ...prev, reason: "" }));
            }}
            rows={8}
          ></textarea>
          {errors.reason && <p className="error">{errors.reason}</p>}
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button className="btn-outlined" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              className={
                actionType === "DISQUALIFIED"
                  ? "btn-outlined btn-reject"
                  : "btn-contained"
              }
              onClick={confirmAction}
              disabled={btnLoading}
            >
              {btnLoading
                ? "Submitting..."
                : actionType === "SUBMITTED"
                  ? "Send for Revision"
                  : actionType === "DISQUALIFIED"
                    ? "Reject"
                    : actionType === "AWARDED"
                      ? "Award"
                      : "Recommend"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Apply Values Modal (UI only; backend support needed) */}

      {/* Rebid Modal */}
      <Modal open={openRebidModal} onClose={() => setOpenRebidModal(false)}>
        <Box sx={style}>
          <Typography
            variant="h6"
            component="h2"
            textAlign="start"
            sx={{ mb: 2 }}
          >
            Request Revised Quotations
          </Typography>

          <label
            htmlFor="rebidStartDate"
            style={{ display: "block", marginBottom: "10px" }}
          >
            Revised Quote Submission Start Date
          </label>
          <input
            type="datetime-local"
            name="rebidStartDate"
            id="rebidStartDate"
            value={rebidData.rebidStartDate}
            onChange={handleRebidChange}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "4px",
              border: errors.rebidStartDate
                ? "2px solid red"
                : "1px solid #ccc",
            }}
          />
          {errors.rebidStartDate && (
            <p className="error">{errors.rebidStartDate}</p>
          )}

          <label
            htmlFor="rebidEndDate"
            style={{
              display: "block",
              marginBottom: "10px",
              marginTop: "15px",
            }}
          >
            Revised Quote Submission End Date
          </label>
          <input
            type="datetime-local"
            name="rebidEndDate"
            id="rebidEndDate"
            value={rebidData.rebidEndDate}
            onChange={handleRebidChange}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "4px",
              border: errors.rebidEndDate ? "2px solid red" : "1px solid #ccc",
            }}
          />
          {errors.rebidEndDate && (
            <p className="error">{errors.rebidEndDate}</p>
          )}

          <label
            htmlFor="rebidComment"
            style={{
              display: "block",
              marginBottom: "10px",
              marginTop: "15px",
            }}
          >
            Comments for Rebid
          </label>
          <textarea
            name="rebidComment"
            id="rebidComment"
            value={rebidData.rebidComment}
            onChange={handleRebidChange}
            rows={4}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />

          <Box className="btn-container" sx={{ gap: 3, width: "100%", mt: 2 }}>
            <Button
              className="btn-outlined"
              onClick={() => setOpenRebidModal(false)}
            >
              Close
            </Button>
            <Button
              className="btn-contained"
              onClick={handleRebidSubmit}
              disabled={btnLoading}
            >
              {btnLoading ? "Submitting..." : "Send Rebid Request"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* E-Auction Modal */}
      <Modal open={openAuction} onClose={() => setOpenAuction(false)}>
        <Box sx={style}>
          <label
            htmlFor="eauctionEndDate"
            style={{
              display: "block",
              marginBottom: "10px",
              marginTop: "15px",
            }}
          >
            Auction End Date
          </label>
          <input
            type="datetime-local"
            name="rebidEndDate"
            id="eauctionEndDate"
            value={rebidData.rebidEndDate}
            onChange={handleRebidChange}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "4px",
              border: errors.rebidEndDate ? "2px solid red" : "1px solid #ccc",
            }}
          />
          {errors.rebidEndDate && (
            <p className="error">{errors.rebidEndDate}</p>
          )}

          <Box className="btn-container" sx={{ gap: 3, width: "100%", mt: 2 }}>
            <Button
              className="btn-outlined"
              onClick={() => setOpenAuction(false)}
            >
              Close
            </Button>
            <Button
              className="btn-contained"
              onClick={handleSendEauctionWithValidation}
              disabled={btnLoading}
            >
              {btnLoading ? "Sending..." : "Send to E-Auction"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <SuccessModal
        open={openSuccess}
        handleClose={() => setOpenSuccess(false)}
        msg={successMsg}
        nav={"/logistic-dashboard"}
      />
      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default ViewLogisticQuotes;
